import React, { ComponentType, createElement, FC, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import colors from '@pickles/shared/utils/colors';
import { FeatureFlagKeys } from '@pickles/shared/redux/feature-flag/constants';

import { FeatureFlagHooks, UserHooks } from '../app/redux/hooks';
import sizes from '../util/sizes';

import { BaseModal } from '../components/Shared/Modal';

type CallbackType<T> = (...args: T[]) => void;

interface WithEInvoicingNudgeOptions {
  callbackPropName: string; // e.g. 'onPress', 'onClick', etc.
};

const withEInvoicingNudge = <P extends object, T = any>(
  WrappedComponent: ComponentType<P>,
  {
    callbackPropName,
  }: WithEInvoicingNudgeOptions
): FC<P> => {
  return (props: P) => {
    const [showModal, setShowModal] = useState(false);
    const [pendingParams, setPendingParams] = useState<T | null>(null);

    const { t } = useTranslation();
    const router = useRouter();
    const account = UserHooks.useUserAccount();

    // Check if the user has provided e-invoicing details using the account type
    const isLoggedIn = !!account;
    const hasEInvoicingDetails = isLoggedIn && !!account?.accType;
    
    const isEInvoicingNudgeEnabled = FeatureFlagHooks.useIsFeatureFlagEnabled(
      FeatureFlagKeys.APP_SHOW_E_INVOICING_NUDGE,
      true,
    );
    const isEInvoicingNudgeSkippable = FeatureFlagHooks.useIsFeatureFlagEnabled(
      FeatureFlagKeys.APP_ENABLE_SKIPPING_E_INVOICING_NUDGE,
      true,
    );

    // Dynamically wrap the original callback
    const originalCallback = (props as any)[callbackPropName] as CallbackType<T> | undefined;

    const wrappedCallback = (params: T) => {
      if (params && typeof params === 'object') {
        // Type guard to check if the object has stopPropagation method
        const event = (params as unknown) as { stopPropagation?: () => void };
        if (event.stopPropagation && typeof event.stopPropagation === 'function') {
          event.stopPropagation();
        }
      }

      if (isLoggedIn && !hasEInvoicingDetails && isEInvoicingNudgeEnabled) {
        // If the feature is enabled and e-invoicing has not been provided,
        // set the pending action and show the modal for nudge message
        setPendingParams(params);
        setShowModal(true);
      } else {
        // Else, just call the original callback
        originalCallback?.(params);
      }
    };

    // Inject the wrapped callback into props
    const injectedProps = {
      ...props,
      [callbackPropName]: wrappedCallback,
    };

    const handleSkip = () => {
      setShowModal(false);
      if (pendingParams) {
        // If there are pending params, call the original callback with them
        originalCallback?.(pendingParams);
      } else {
        // If no pending params, just call the original callback without params
        originalCallback?.();
      }
      setPendingParams(null);
    };

    const handleCancel = () => {
      setShowModal(false);
      setPendingParams(null);
    };

    const handleProvideInfo = () => {
      setShowModal(false);
      router.push({
        pathname: '/profile',
        query: { 
          section: 'eInvoicingDetails', // TODO: Fix this to use SectionNames enum. Hardcoded for now due to circular dependency issues
        }
      }, '/profile');
    };

    return (
      <>
        {createElement(WrappedComponent, injectedProps as P)}
        <BaseModal
          isOpened={showModal}
          onClickOutside={() => {}}
          id="e-invoicing-nudge-modal"
        >
          <ModalContainer>
            <MessageText>
              {t('infos:e_invoicing_nudge')}
            </MessageText>
            <ButtonsContainer>
              {isEInvoicingNudgeSkippable
                ? (
                  <CloseButton onClick={handleSkip}>
                    {t('buttons:skip')}
                  </CloseButton>
                ): (
                  <CloseButton onClick={handleCancel}>
                    {t('buttons:cancel')}
                  </CloseButton>
                )
              }
              <ProvideInfoButton onClick={handleProvideInfo}>
                {t('buttons:provide_info')}
              </ProvideInfoButton>
            </ButtonsContainer>
          </ModalContainer>
        </BaseModal>
      </>
    )
  };
};

export default withEInvoicingNudge;

const ModalContainer = styled.div`
  font-size: 13px;
  color: ${colors.new_gray};
  width: 380px;
  > * {
    font-family: BasierCircle;
  }

  @media (max-width: ${sizes.M}) {
    width: 100%;
    padding: 0 20px;
  }

  @media (max-width: ${sizes.S}) {
    padding: 0;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const MessageText = styled.div`
  margin-bottom: 30px;
  color: ${colors.san_juan_grey};
  font-size: 16px;
  > div {
    margin-bottom: 4px;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border-radius: 8px;
  padding: 12px 28px 14px;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.new_gray};
  border: none;
  max-width: 40%;
  cursor: pointer;
  display: block;

  @media (max-width: ${sizes.M}) {
    width: calc(100% - 40px);
  }
`;

const ProvideInfoButton = styled.button`
  background: ${colors.blumine_main_blue};
  border-radius: 8px;
  padding: 12px 28px 14px;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.white};
  border: none;
  max-width: 40%;
  cursor: pointer;
  display: block;

  @media (max-width: ${sizes.M}) {
    width: calc(100% - 40px);
  }
`;